import * as THREE from 'three';
import { NetworkStats } from '../../core/models/NetworkStats';
import { useGalaxyStore } from '../../core/state/GalaxyStore';
import { SolarFlares } from './SolarFlares';
import { MempoolNebula } from './MempoolNebula';
import { TrailEffect } from './TrailEffect';
import { ActivityRing } from './ActivityRing';
import { EngineGlow } from './EngineGlow';
import { FeeZoneManager } from './FeeZoneManager';
import { QualityManager } from './QualityManager';
import { InteractionEffects } from './InteractionEffects';

export type QualityLevel = 'low' | 'medium' | 'high' | 'ultra';

export interface EffectsConfig {
  targetFPS: number;
  effectsBudget: number; // milliseconds per frame
  autoQualityAdjustment: boolean;
  reducedMotion: boolean;
}

export interface NetworkEffectsData {
  transactionRate: number;
  mempoolSize: number;
  averageFeeRate: number;
  hashrate: number;
  difficulty: number;
  blockHeight: number;
}

export interface InteractionEvent {
  type: 'hover' | 'click' | 'select' | 'camera_transition';
  object?: THREE.Object3D;
  position?: THREE.Vector3;
  intensity?: number;
}

export class EffectsManager {
  private scene: THREE.Scene;
  private renderer: THREE.WebGLRenderer;
  private camera: THREE.Camera;
  private config: EffectsConfig;
  
  // Effect systems
  public qualityManager: QualityManager;
  public interactionEffects: InteractionEffects;
  
  // Individual effects (existing)
  private solarFlares?: SolarFlares;
  private mempoolNebula?: MempoolNebula;
  private trailEffect?: TrailEffect;
  private activityRing?: ActivityRing;
  private engineGlow?: EngineGlow;
  private feeZoneManager?: FeeZoneManager;
  
  // State management
  private storeUnsubscribe?: () => void;
  private lastNetworkData?: NetworkEffectsData;
  private performanceHistory: number[] = [];
  private lastFrameTime = 0;
  private performanceMonitoringStartTime = 0;
  
  // Quality settings
  private currentQuality: QualityLevel = 'medium';
  private autoQualityEnabled = true;
  
  constructor(
    scene: THREE.Scene,
    renderer: THREE.WebGLRenderer,
    camera: THREE.Camera,
    config: Partial<EffectsConfig> = {}
  ) {
    this.scene = scene;
    this.renderer = renderer;
    this.camera = camera;
    
    this.config = {
      targetFPS: 60,
      effectsBudget: 8, // 8ms budget for effects
      autoQualityAdjustment: false, // Disabled by default to prevent aggressive quality reduction
      reducedMotion: false,
      ...config
    };
    
    // Initialize subsystems
    this.qualityManager = new QualityManager(this.config);
    this.interactionEffects = new InteractionEffects(scene, renderer);
    
    // Detect device and set initial quality
    this.currentQuality = this.qualityManager.getDefaultQuality();
    this.autoQualityEnabled = this.config.autoQualityAdjustment;
    
    this.initialize();
  }
  
  private initialize(): void {
    this.connectToStore();
    this.setupPerformanceMonitoring();
  }
  
  public registerEffect(name: string, effect: any): void {
    switch (name) {
      case 'solarFlares':
        this.solarFlares = effect;
        break;
      case 'mempoolNebula':
        this.mempoolNebula = effect;
        break;
      case 'trailEffect':
        this.trailEffect = effect;
        break;
      case 'activityRing':
        this.activityRing = effect;
        break;
      case 'engineGlow':
        this.engineGlow = effect;
        break;
      case 'feeZoneManager':
        this.feeZoneManager = effect;
        break;
    }
    
    // Apply current quality to newly registered effect
    if (effect && typeof effect.setQualityLevel === 'function') {
      effect.setQualityLevel(this.currentQuality);
    }
  }
  
  private connectToStore(): void {
    const store = useGalaxyStore.getState();
    
    // Subscribe to store updates for real-time effects
    this.storeUnsubscribe = useGalaxyStore.subscribe(
      (state) => ({
        network: state.network,
        mempool: state.mempool,
        blockchain: state.blockchain
      }),
      (newState) => {
        this.updateFromStoreData(newState);
      }
    );
    
    // Apply initial state
    this.updateFromStoreData({
      network: store.network,
      mempool: store.mempool,
      blockchain: store.blockchain
    });
  }
  
  private updateFromStoreData(storeData: any): void {
    const networkData: NetworkEffectsData = {
      transactionRate: this.calculateTransactionRate(storeData.blockchain),
      mempoolSize: storeData.mempool.size || 0,
      averageFeeRate: storeData.mempool.averageFeeRate || 0,
      hashrate: storeData.network.hashrate || 0,
      difficulty: storeData.network.difficulty || 0,
      blockHeight: storeData.network.currentHeight || 0
    };
    
    this.updateEffectsFromNetworkData(networkData);
    this.lastNetworkData = networkData;
  }
  
  private calculateTransactionRate(blockchain: any): number {
    if (!blockchain.transactions) return 0;
    
    const transactions = Array.from(blockchain.transactions.values());
    const now = Date.now();
    const recentTxs = transactions.filter(
      (tx: any) => now - tx.spawnedAt < 60000 // Last minute
    );
    
    return recentTxs.length / 60; // transactions per second
  }
  
  public updateEffectsFromNetworkData(data: NetworkEffectsData): void {
    // Update SolarFlares based on transaction activity and hashrate
    if (this.solarFlares) {
      const activityLevel = Math.min(data.transactionRate / 10, 1.0);
      const hashrateIntensity = Math.min(data.hashrate / 1e20, 1.0);
      
      this.solarFlares.updateActivity(activityLevel);
      this.solarFlares.setIntensity(hashrateIntensity);
      this.solarFlares.setFrequency(activityLevel * 2);
    }
    
    // Update MempoolNebula based on mempool congestion
    if (this.mempoolNebula && typeof this.mempoolNebula.updateFromMempoolStats === 'function') {
      this.mempoolNebula.updateFromMempoolStats({
        size: data.mempoolSize,
        count: Math.floor(data.mempoolSize / 100), // Estimate tx count
        feeHistogram: this.generateFeeHistogram(data.averageFeeRate),
        avgFeeRate: data.averageFeeRate
      });
    }
    
    // Update FeeZoneManager with current fee rates
    if (this.feeZoneManager && data.averageFeeRate > 0) {
      // FeeZoneManager will be updated through its own data integration
      // Note: Add updateFeeHistogram method to FeeZoneManager if needed
    }
  }
  
  private calculateCongestionLevel(mempoolSize: number): number {
    // Normalize mempool size to congestion level (0-1)
    const maxMempool = 100000; // Assume max reasonable mempool size
    return Math.min(mempoolSize / maxMempool, 1.0);
  }
  
  private generateFeeHistogram(averageFeeRate: number): any[] {
    // Generate a simple fee histogram for visualization
    return [
      { feeRate: averageFeeRate * 0.5, count: 30 },
      { feeRate: averageFeeRate, count: 50 },
      { feeRate: averageFeeRate * 1.5, count: 20 },
      { feeRate: averageFeeRate * 2, count: 10 }
    ];
  }
  
  public handleInteraction(event: InteractionEvent): void {
    this.interactionEffects.handleInteraction(event);
    
    // Trigger responsive effects based on interaction
    if (event.type === 'click' && this.solarFlares) {
      this.solarFlares.triggerActivityFlare(1.0);
    }
  }
  
  private setupPerformanceMonitoring(): void {
    // Wait 3 seconds before starting performance monitoring to allow system to stabilize
    setTimeout(() => {
      this.performanceMonitoringStartTime = performance.now();
      
      // Monitor frame times for automatic quality adjustment
      const measurePerformance = () => {
        const currentTime = performance.now();
        
        // Only start collecting performance data after stabilization period
        if (currentTime - this.performanceMonitoringStartTime > 2000) {
          if (this.lastFrameTime > 0) {
            const frameTime = currentTime - this.lastFrameTime;
            this.performanceHistory.push(frameTime);
            
            // Keep last 60 frames
            if (this.performanceHistory.length > 60) {
              this.performanceHistory.shift();
            }
            
            // Check if quality adjustment is needed (wait for more samples to stabilize)
            if (this.performanceHistory.length >= 60 && this.autoQualityEnabled) {
              this.checkPerformanceAndAdjustQuality();
            }
          }
        }
        this.lastFrameTime = currentTime;
        
        requestAnimationFrame(measurePerformance);
      };
      
      requestAnimationFrame(measurePerformance);
    }, 3000);
  }
  
  private checkPerformanceAndAdjustQuality(): void {
    const averageFrameTime = this.performanceHistory.reduce((a, b) => a + b, 0) / this.performanceHistory.length;
    const currentFPS = 1000 / averageFrameTime;
    
    // Use more conservative thresholds to avoid unnecessary quality changes
    if (currentFPS < this.config.targetFPS - 15 && this.currentQuality !== 'low') {
      // Performance is significantly poor, reduce quality
      this.reduceQuality();
    } else if (currentFPS > this.config.targetFPS + 10 && this.currentQuality !== 'ultra') {
      // Performance is consistently good, consider increasing quality
      this.increaseQuality();
    }
  }
  
  private reduceQuality(): void {
    const qualityLevels: QualityLevel[] = ['ultra', 'high', 'medium', 'low'];
    const currentIndex = qualityLevels.indexOf(this.currentQuality);
    
    if (currentIndex < qualityLevels.length - 1) {
      this.setQuality(qualityLevels[currentIndex + 1]);
      console.log(`[EffectsManager] Reduced quality to ${this.currentQuality} due to performance`);
    }
  }
  
  private increaseQuality(): void {
    const qualityLevels: QualityLevel[] = ['low', 'medium', 'high', 'ultra'];
    const currentIndex = qualityLevels.indexOf(this.currentQuality);
    
    if (currentIndex < qualityLevels.length - 1) {
      this.setQuality(qualityLevels[currentIndex + 1]);
      console.log(`[EffectsManager] Increased quality to ${this.currentQuality} due to good performance`);
    }
  }
  
  public setQuality(quality: QualityLevel): void {
    this.currentQuality = quality;
    
    // Apply quality to all registered effects
    const effects = [
      this.solarFlares,
      this.mempoolNebula,
      this.trailEffect,
      this.activityRing,
      this.engineGlow,
      this.feeZoneManager
    ];
    
    effects.forEach(effect => {
      if (effect && 'setQualityLevel' in effect && typeof effect.setQualityLevel === 'function') {
        effect.setQualityLevel(quality);
      }
    });
    
    // Update interaction effects quality
    this.interactionEffects.setQuality(quality);
    
    // Update quality manager
    this.qualityManager.setCurrentQuality(quality);
  }
  
  public setAutoQualityEnabled(enabled: boolean): void {
    this.autoQualityEnabled = enabled;
  }
  
  public getCurrentQuality(): QualityLevel {
    return this.currentQuality;
  }
  
  public getPerformanceStats(): {
    currentFPS: number;
    averageFrameTime: number;
    quality: QualityLevel;
    effectsCount: number;
  } {
    const averageFrameTime = this.performanceHistory.length > 0
      ? this.performanceHistory.reduce((a, b) => a + b, 0) / this.performanceHistory.length
      : 16.67;
    
    const effectsCount = [
      this.solarFlares,
      this.mempoolNebula,
      this.trailEffect,
      this.activityRing,
      this.engineGlow,
      this.feeZoneManager
    ].filter(effect => effect !== undefined).length;
    
    return {
      currentFPS: 1000 / averageFrameTime,
      averageFrameTime,
      quality: this.currentQuality,
      effectsCount
    };
  }
  
  public update(deltaTime: number): void {
    // Update all registered effects
    if (this.solarFlares) this.solarFlares.update(deltaTime);
    if (this.mempoolNebula && typeof this.mempoolNebula.update === 'function') {
      this.mempoolNebula.update(deltaTime);
    }
    if (this.trailEffect && typeof this.trailEffect.update === 'function') {
      this.trailEffect.update(deltaTime);
    }
    if (this.activityRing && typeof this.activityRing.update === 'function') {
      this.activityRing.update(deltaTime);
    }
    if (this.engineGlow && typeof this.engineGlow.update === 'function') {
      this.engineGlow.update(deltaTime);
    }
    if (this.feeZoneManager && typeof this.feeZoneManager.update === 'function') {
      this.feeZoneManager.update(deltaTime);
    }
    
    // Update interaction effects
    this.interactionEffects.update(deltaTime);
  }
  
  public dispose(): void {
    // Unsubscribe from store
    if (this.storeUnsubscribe) {
      this.storeUnsubscribe();
    }
    
    // Dispose subsystems
    this.qualityManager.dispose();
    this.interactionEffects.dispose();
    
    // Clear performance history
    this.performanceHistory = [];
  }
}